"use client";

import { useState, useCallback, useMemo, FormEvent } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { showAuthSuccess, showAuthError } from "@/lib/toast";
import Link from "next/link";
import Image from "next/image";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaBuildingColumns,
  FaChevronDown,
  FaSpinner,
  FaCircleCheck,
  FaArrowRight,
  FaUsers,
  FaBookOpen,
  FaHandshake,
} from "react-icons/fa6";
import { FaShieldAlt } from "react-icons/fa";
import useAuthStore from "@/zustand/authStore";
import { IChapter } from "@/models/Chapter";

const SimpleAuthForm = ({ chapters = [] }: { chapters?: IChapter[] }) => {
  const searchParams = useSearchParams();
  const router = useRouter();

  const mode = searchParams.get("mode");
  const initialTab = mode === "login" ? "login" : "signup";

  const [activeTab, setActiveTab] = useState(initialTab);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Modal states
  const [showEmailVerificationModal, setShowEmailVerificationModal] =
    useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");

  const { register, login, user } = useAuthStore();

  // Form data
  const [formData, setFormData] = useState({
    fullname: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    terms: false,
    chapter: "",
  });

  const [loginFormData, setLoginFormData] = useState({
    email: "",
    password: "",
  });

  // Validation
  const emailRegex = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/, []);
  const phoneRegex = useMemo(() => /^\+?[0-9\s\-]+$/, []);

  const validateSignupForm = useCallback(() => {
    const newErrors: Record<string, string> = {};

    // ✅ SECURITY FIX: Improved full name validation
    if (!formData.fullname.trim()) {
      newErrors.fullname = "Full name is required";
    } else if (formData.fullname.trim().length < 2) {
      newErrors.fullname = "Full name must be at least 2 characters";
    } else if (formData.fullname.trim().length > 50) {
      newErrors.fullname = "Full name cannot exceed 50 characters";
    } else if (!/^[a-zA-Z\s\-']+$/.test(formData.fullname.trim())) {
      newErrors.fullname =
        "Full name can only contain letters, spaces, hyphens, and apostrophes";
    } else {
      const words = formData.fullname.trim().split(/\s+/);
      if (words.length < 2) {
        newErrors.fullname = "Please enter your first and last name";
      }
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (formData.phone && !phoneRegex.test(formData.phone)) {
      newErrors.phone = "Please enter a valid phone number";
    }
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password =
        "Password must contain uppercase, lowercase, and numbers";
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }
    if (!formData.chapter) newErrors.chapter = "Please select a chapter";
    if (!formData.terms)
      newErrors.terms = "You must agree to the terms and conditions";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, emailRegex, phoneRegex]);

  const validateLoginForm = useCallback(() => {
    const newErrors: Record<string, string> = {};

    if (!loginFormData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(loginFormData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!loginFormData.password) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [loginFormData, emailRegex]);

  // Handlers
  const handleSignupSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();

      if (!validateSignupForm()) return;

      setIsLoading(true);
      setErrors({});

      try {
        const userData = {
          fullName: formData.fullname,
          email: formData.email,
          phone: formData.phone,
          role: "student",
          password: formData.password,
          chapter: formData.chapter,
        };

        await register(userData);
        setVerificationEmail(formData.email);
        setShowEmailVerificationModal(true);

        // Reset form
        setFormData({
          fullname: "",
          email: "",
          phone: "",
          password: "",
          confirmPassword: "",
          terms: false,
          chapter: "",
        });

        showAuthSuccess(
          "Registration successful! Please check your email to verify your account."
        );
      } catch (err: any) {
        showAuthError(err);
      } finally {
        setIsLoading(false);
      }
    },
    [formData, validateSignupForm, register],
  );

  const handleLoginSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();

      if (!validateLoginForm()) return;

      setIsLoading(true);
      setErrors({});

      try {
        const response = await login(
          loginFormData.email,
          loginFormData.password,
        );
        console.log("Login response:", response);
        showAuthSuccess("Login successful! Redirecting...");
        const redirectUrl =
          response === "student"
            ? "/user"
            : response === "alumni"
              ? "/alumni/dashboard"
              : response === "chapter-admin"
                ? "/chapter"
                : "/admin";

        router.push(redirectUrl);
      } catch (err: any) {
        showAuthError(err);
        const errorMessage = err.response?.data?.message || err.message || "Login failed";
        setErrors({ general: errorMessage });
      } finally {
        setIsLoading(false);
      }
    },
    [loginFormData, validateLoginForm, login, router],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value, type } = e.target;

      setFormData((prev) => ({
        ...prev,
        [name]:
          type === "checkbox" && e.target instanceof HTMLInputElement
            ? e.target.checked
            : value,
      }));

      // Clear error when user types
      if (errors[name]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    },
    [errors],
  );

  const handleLoginInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;

      setLoginFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      // Clear error when user types
      if (errors[name]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    },
    [errors],
  );

  const handleTabChange = useCallback((tab: string) => {
    setActiveTab(tab);
    setErrors({});
  }, []);

  // Chapter options
  const chapterOptions = chapters.map((chapter) => (
    <option key={String(chapter._id)} value={String(chapter._id)}>
      {chapter.name}
    </option>
  ));
  console.log(chapters);

  const getPasswordStrength = (password: string) => {
    if (password.length === 0) return { text: "", color: "" };
    if (password.length < 6) return { text: "Weak", color: "text-red-600" };
    if (password.length < 8) return { text: "Good", color: "text-yellow-600" };
    return { text: "Strong", color: "text-green-600" };
  };

  const passwordStrength = getPasswordStrength(formData.password);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-blue-50 flex">
      {/* Left Side - Branding & Info */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary-700 to-primary-900 p-12 flex-col justify-between relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-400 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10">
          <Link href="/" className="inline-block mb-8">
            <div className="w-48 h-20 relative">
              <Image
                src="/images/logo/logo_blue_horizontal.png"
                alt="CONCES Logo"
                fill
                className="object-contain"
                priority
              />
            </div>
          </Link>

          <div className="mt-12">
            <h1 className="text-4xl font-bold text-white mb-4">
              {activeTab === "login"
                ? "Welcome Back!"
                : "Join the CONCES Community"}
            </h1>
            <p className="text-xl text-white/90 leading-relaxed mb-8">
              {activeTab === "login"
                ? "Continue your journey with fellow Christian engineers"
                : "Connect, learn, and grow with a community of faith-driven engineering professionals"}
            </p>
          </div>

          {/* Features List */}
          <div className="space-y-4 mt-12">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mr-4">
                <FaBookOpen className="text-white text-xl" />
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">
                  Learning Resources
                </h3>
                <p className="text-white/80 text-sm">
                  Access course materials and study resources
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mr-4">
                <FaUsers className="text-white text-xl" />
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">
                  Networking Events
                </h3>
                <p className="text-white/80 text-sm">
                  Connect with chapter members and alumni
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mr-4">
                <FaHandshake className="text-white text-xl" />
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">
                  Mentorship Programs
                </h3>
                <p className="text-white/80 text-sm">
                  Get guidance from experienced professionals
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mr-4">
                <FaShieldAlt className="text-white text-xl" />
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">
                  Secure Platform
                </h3>
                <p className="text-white/80 text-sm">
                  Your data is protected with enterprise-grade security
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10 text-white/80 text-sm">
          <p>© 2024 CONCES. All rights reserved.</p>
        </div>
      </div>

      {/* Right Side - Auth Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <Link href="/" className="inline-block">
              <div className="w-40 h-16 relative mx-auto">
                <Image
                  src="/images/logo/logo_blue_horizontal.png"
                  alt="CONCES Logo"
                  fill
                  className="object-contain"
                  priority
                />
              </div>
            </Link>
          </div>

          {/* Tab Navigation */}
          <div className="mb-8">
            <div className="flex bg-gray-100 rounded-xl p-1.5">
              <button
                onClick={() => handleTabChange("login")}
                className={`flex-1 py-3 px-4 text-sm font-semibold rounded-lg transition-all duration-200 ${
                  activeTab === "login"
                    ? "bg-white text-primary-700 shadow-md"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Sign In
              </button>
              <button
                onClick={() => handleTabChange("signup")}
                className={`flex-1 py-3 px-4 text-sm font-semibold rounded-lg transition-all duration-200 ${
                  activeTab === "signup"
                    ? "bg-white text-primary-700 shadow-md"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Sign Up
              </button>
            </div>
          </div>

          {/* Forms Container */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 sm:p-8">
            {/* Header */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {activeTab === "login" ? "Welcome Back" : "Create Account"}
              </h2>
              <p className="text-gray-600">
                {activeTab === "login"
                  ? "Sign in to continue to your dashboard"
                  : "Join CONCES and start your journey"}
              </p>
            </div>

            {/* Alumni CTA */}
            {activeTab === "signup" && (
              <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-purple-900 mb-1">
                      Are you an alumnus?
                    </h4>
                    <p className="text-xs text-purple-700">
                      Join our exclusive alumni network
                    </p>
                  </div>
                  <Link
                    href="/alumni/join"
                    className="ml-4 px-4 py-2 bg-purple-600 text-white text-xs font-semibold rounded-lg hover:bg-purple-700 transition-colors whitespace-nowrap flex items-center"
                  >
                    Join Alumni
                    <FaArrowRight className="ml-1.5" size={12} />
                  </Link>
                </div>
              </div>
            )}

            {/* Login Form */}
            {activeTab === "login" && (
              <form onSubmit={handleLoginSubmit} className="space-y-5">
                {errors.general && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{errors.general}</p>
                  </div>
                )}

                <div>
                  <label
                    htmlFor="login-email"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <FaEnvelope className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="login-email"
                      name="email"
                      type="email"
                      value={loginFormData.email}
                      onChange={handleLoginInputChange}
                      className={`pl-12 pr-4 py-3 w-full rounded-xl border-2 transition-all ${
                        errors.email
                          ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                          : "border-gray-200 focus:border-primary-500 focus:ring-primary-200"
                      } focus:outline-none focus:ring-4`}
                      placeholder="you@example.com"
                      required
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1.5 text-sm text-red-600 flex items-center">
                      <span className="mr-1">•</span>
                      {errors.email}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="login-password"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <FaLock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="login-password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={loginFormData.password}
                      onChange={handleLoginInputChange}
                      className={`pl-12 pr-12 py-3 w-full rounded-xl border-2 transition-all ${
                        errors.password
                          ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                          : "border-gray-200 focus:border-primary-500 focus:ring-primary-200"
                      } focus:outline-none focus:ring-4`}
                      placeholder="Enter your password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? (
                        <FaEyeSlash className="h-5 w-5" />
                      ) : (
                        <FaEye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1.5 text-sm text-red-600 flex items-center">
                      <span className="mr-1">•</span>
                      {errors.password}
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <Link
                    href="/auth/forgotten-password"
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center items-center py-3.5 px-4 border border-transparent rounded-xl shadow-lg text-base font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 focus:outline-none focus:ring-4 focus:ring-primary-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-0.5"
                >
                  {isLoading ? (
                    <>
                      <FaSpinner className="animate-spin mr-2" />
                      Signing In...
                    </>
                  ) : (
                    <>
                      Sign In
                      <FaArrowRight className="ml-2" />
                    </>
                  )}
                </button>
              </form>
            )}

            {/* Signup Form */}
            {activeTab === "signup" && (
              <form onSubmit={handleSignupSubmit} className="space-y-5">
                <div>
                  <label
                    htmlFor="fullname"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <FaUser className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="fullname"
                      name="fullname"
                      type="text"
                      value={formData.fullname}
                      onChange={handleInputChange}
                      className={`pl-12 pr-4 py-3 w-full rounded-xl border-2 transition-all ${
                        errors.fullname
                          ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                          : "border-gray-200 focus:border-primary-500 focus:ring-primary-200"
                      } focus:outline-none focus:ring-4`}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  {errors.fullname && (
                    <p className="mt-1.5 text-sm text-red-600 flex items-center">
                      <span className="mr-1">•</span>
                      {errors.fullname}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <FaEnvelope className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`pl-12 pr-4 py-3 w-full rounded-xl border-2 transition-all ${
                        errors.email
                          ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                          : "border-gray-200 focus:border-primary-500 focus:ring-primary-200"
                      } focus:outline-none focus:ring-4`}
                      placeholder="you@example.com"
                      required
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1.5 text-sm text-red-600 flex items-center">
                      <span className="mr-1">•</span>
                      {errors.email}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Phone Number
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <FaPhone className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`pl-12 pr-4 py-3 w-full rounded-xl border-2 transition-all ${
                        errors.phone
                          ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                          : "border-gray-200 focus:border-primary-500 focus:ring-primary-200"
                      } focus:outline-none focus:ring-4`}
                      placeholder="+234 123 456 7890"
                    />
                  </div>
                  {errors.phone && (
                    <p className="mt-1.5 text-sm text-red-600 flex items-center">
                      <span className="mr-1">•</span>
                      {errors.phone}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="chapter"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Chapter <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <FaBuildingColumns className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                      id="chapter"
                      name="chapter"
                      value={formData.chapter}
                      onChange={handleInputChange}
                      className={`pl-12 pr-10 py-3 w-full rounded-xl border-2 transition-all appearance-none bg-white ${
                        errors.chapter
                          ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                          : "border-gray-200 focus:border-primary-500 focus:ring-primary-200"
                      } focus:outline-none focus:ring-4`}
                      required
                    >
                      <option value="">Select your chapter</option>
                      {chapterOptions}
                    </select>
                    <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                      <FaChevronDown className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                  {errors.chapter && (
                    <p className="mt-1.5 text-sm text-red-600 flex items-center">
                      <span className="mr-1">•</span>
                      {errors.chapter}
                    </p>
                  )}
                  {/* Helpful note when a chapter isn't listed */}
                  <p className="mt-2 text-sm text-gray-600">
                    Can't find your chapter here?{" "}
                    <Link
                      href="/contact-us?subject=chapter"
                      className="text-primary-600 hover:underline font-semibold"
                    >
                      Contact us
                    </Link>{" "}
                    and we'll help add it.
                  </p>
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <FaLock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={handleInputChange}
                      className={`pl-12 pr-12 py-3 w-full rounded-xl border-2 transition-all ${
                        errors.password
                          ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                          : "border-gray-200 focus:border-primary-500 focus:ring-primary-200"
                      } focus:outline-none focus:ring-4`}
                      placeholder="Create a strong password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? (
                        <FaEyeSlash className="h-5 w-5" />
                      ) : (
                        <FaEye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1.5 text-sm text-red-600 flex items-center">
                      <span className="mr-1">•</span>
                      {errors.password}
                    </p>
                  )}
                  {formData.password && (
                    <p
                      className={`mt-1.5 text-xs font-medium ${passwordStrength.color}`}
                    >
                      Password strength: {passwordStrength.text}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Confirm Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <FaLock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className={`pl-12 pr-12 py-3 w-full rounded-xl border-2 transition-all ${
                        errors.confirmPassword
                          ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                          : "border-gray-200 focus:border-primary-500 focus:ring-primary-200"
                      } focus:outline-none focus:ring-4`}
                      placeholder="Confirm your password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showConfirmPassword ? (
                        <FaEyeSlash className="h-5 w-5" />
                      ) : (
                        <FaEye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-1.5 text-sm text-red-600 flex items-center">
                      <span className="mr-1">•</span>
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>

                <div className="flex items-start">
                  <input
                    id="terms"
                    name="terms"
                    type="checkbox"
                    checked={formData.terms}
                    onChange={handleInputChange}
                    className={`mt-1 h-4 w-4 text-primary-600 border-2 rounded focus:ring-primary-500 ${
                      errors.terms ? "border-red-300" : "border-gray-300"
                    }`}
                  />
                  <label
                    htmlFor="terms"
                    className="ml-3 block text-sm text-gray-700"
                  >
                    I agree to the{" "}
                    <Link
                      href="/privacy"
                      className="text-primary-600 hover:text-primary-700 font-semibold"
                    >
                      Privacy Policy
                    </Link>{" "}
                    and{" "}
                    <Link
                      href="/terms"
                      className="text-primary-600 hover:text-primary-700 font-semibold"
                    >
                      Terms of Service
                    </Link>
                  </label>
                </div>
                {errors.terms && (
                  <p className="mt-1.5 text-sm text-red-600 flex items-center">
                    <span className="mr-1">•</span>
                    {errors.terms}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center items-center py-3.5 px-4 border border-transparent rounded-xl shadow-lg text-base font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 focus:outline-none focus:ring-4 focus:ring-primary-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-0.5"
                >
                  {isLoading ? (
                    <>
                      <FaSpinner className="animate-spin mr-2" />
                      Creating Account...
                    </>
                  ) : (
                    <>
                      Create Account
                      <FaArrowRight className="ml-2" />
                    </>
                  )}
                </button>

                {/* Benefits Card */}
                <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                  <div className="flex items-start">
                    <FaCircleCheck className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="font-semibold text-blue-900 mb-2">
                        As a student, you'll get access to:
                      </p>
                      <ul className="text-blue-800 space-y-1.5">
                        <li className="flex items-center">
                          <span className="mr-2">•</span>
                          Course materials and study resources
                        </li>
                        <li className="flex items-center">
                          <span className="mr-2">•</span>
                          Chapter events and networking
                        </li>
                        <li className="flex items-center">
                          <span className="mr-2">•</span>
                          Mentorship opportunities
                        </li>
                        <li className="flex items-center">
                          <span className="mr-2">•</span>
                          Daily devotionals and prayer support
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </form>
            )}
          </div>

          {/* Back to Home */}
          <div className="mt-6 text-center">
            <Link
              href="/"
              className="text-sm text-gray-600 hover:text-primary-600 transition-colors inline-flex items-center"
            >
              <FaArrowRight className="mr-1 rotate-180" size={12} />
              Back to home
            </Link>
          </div>
        </div>
      </div>

      {/* Email Verification Modal */}
      {showEmailVerificationModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 border border-gray-200">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaCircleCheck className="text-green-600 text-3xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Check Your Email
              </h3>
              <p className="text-gray-600">
                We've sent a verification email to
              </p>
              <p className="text-primary-600 font-semibold mt-1">
                {verificationEmail}
              </p>
            </div>
            <div className="bg-blue-50 rounded-xl p-4 mb-6">
              <p className="text-sm text-blue-800">
                Please check your inbox and click the verification link to
                activate your account. If you don't see the email, check your
                spam folder.
              </p>
            </div>
            <button
              onClick={() => setShowEmailVerificationModal(false)}
              className="w-full py-3 px-4 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors shadow-lg"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimpleAuthForm;
